import axios from 'axios';
import baseURL from '@/API';
import axiosInstance from '@/utils/axiosConfig';

const API_URL = baseURL + '/inventaire';

interface ErrorResponse {
    status: number;
    message: string;
}

const DetailInventaireService = {

    /**
     * Récupère tous les détails d'inventaire avec pagination
     * @param url - URL complète avec paramètres (format: /inventaire_detail_emplacement/{id}/?page=1&pageSize=10)
     * @returns Données paginées ou erreur
     */
    async getAll(url: string) {
        try {
            // Construire l'URL complète
            let fullUrl;
            if (url.startsWith('http')) {
                fullUrl = url;
            } else if (url.startsWith('/inventaire/inventaire_detail_emplacement/')) {
                // URL absolue fournie
                fullUrl = `${baseURL}${url}`;
            } else {
                // URL relative fournie (commence par ?)
                // L'ID doit être extrait de l'URL ou passé via un autre mécanisme
                // Pour l'instant, l'URL doit contenir le chemin complet
                fullUrl = url.startsWith('?') ? `${API_URL}/inventaire_detail_emplacement/${url}` : `${baseURL}${url}`;
            }
            
            console.log('🔍 DETAIL INVENTAIRE SERVICE - URL complète:', fullUrl);
            
            const response = await axiosInstance.get(fullUrl);
            
            return { 
                data: response.data.results || response.data.data, 
                totalRows: response.data.recordsFiltered || response.data.count,
                recordsFiltered: response.data.recordsFiltered || response.data.count,
                recordsTotal: response.data.recordsTotal || response.data.count,
                draw: response.data.draw
            };
        } catch (error) {
            console.error('Erreur dans DetailInventaireService.getAll:', error);
            if (axios.isAxiosError(error)) {
                return {
                    status: error.response?.status ?? 500,
                    message: error.response?.data?.message ?? 'An error occurred while fetching inventaire details.'
                };
            }
            return {
                status: 500,
                message: 'An unexpected error occurred.'
            };
        }
    },

    /**
     * Récupère les informations d'un inventaire
     * @param id - ID de l'inventaire
     * @returns Informations de l'inventaire ou erreur
     */
    async getInfo(id: number) {
        try {
            console.log('🔍 DETAIL INVENTAIRE SERVICE - getInfo ID:', id);
            
            const response = await axiosInstance.get(`${API_URL}/info/${id}/`);
            return response.data;
        } catch (error) {
            console.error('Erreur dans DetailInventaireService.getInfo:', error);
            if (axios.isAxiosError(error)) {
                return {
                    status: error.response?.status ?? 500,
                    message: error.response?.data?.message ?? 'An error occurred while fetching inventaire info.'
                };
            }
            return {
                status: 500,
                message: 'An unexpected error occurred.'
            };
        }
    },

    /**
     * Exporte les détails d'inventaire en Excel
     * @param id - ID de l'inventaire
     * @param page - Numéro de page
     * @param pageSize - Taille de la page
     * @param sortBy - Champ de tri
     * @param sortOrder - Ordre de tri
     * @param search - Terme de recherche
     * @returns Blob (fichier Excel) ou erreur
     */
    async extractAll({ id, page, pageSize, sortBy, sortOrder, search }: { 
        id: number; 
        page: number; 
        pageSize: number; 
        sortBy: string; 
        sortOrder: string; 
        search: string 
    }): Promise<any | ErrorResponse> {
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                pageSize: pageSize.toString(),
                ordering: `${sortOrder === 'desc' ? '-' : ''}${sortBy}`,
                search: search,
                export: 'excel'
            });
            
            console.log('🔍 DETAIL INVENTAIRE SERVICE - extractAll URL:', `${API_URL}/detail-inventaire/${id}/?${params.toString()}`);
            
            const response = await axiosInstance.get(`${API_URL}/detail-inventaire/${id}/?${params.toString()}`, {
                responseType: 'blob'
            });
            
            return response;
        } catch (error) {
            console.error('Erreur dans DetailInventaireService.extractAll:', error);
            if (axios.isAxiosError(error)) {
                return {
                    status: error.response?.status ?? 500,
                    message: error.response?.data?.message ?? 'An error occurred while extracting inventaire details.'
                };
            }
            return {
                status: 500,
                message: 'An unexpected error occurred.'
            };
        }
    },

    async getDetailInventaireEmplacement({ id, page, pageSize, sortBy, sortOrder, search }: { 
        id: number; 
        page: number; 
        pageSize: number; 
        sortBy: string; 
        sortOrder: string; 
        search: string 
    }): Promise<any | ErrorResponse> {
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                pageSize: pageSize.toString(),
                ordering: `${sortOrder === 'desc' ? '-' : ''}${sortBy}`,
                search: search,
            });
            
            console.log('🔍 DETAIL INVENTAIRE SERVICE - getDetailInventaireEmplacement URL:', `${API_URL}/detail-inventaire/${id}/?${params.toString()}`);
            
            const response = await axiosInstance.get(`${API_URL}/detail-inventaire/${id}/?${params.toString()}`);
            
            // Retourner les données formatées pour le DataTable
            return {
                data: response.data.results || response.data.data || [],
                totalRows: response.data.recordsFiltered || response.data.count || 0,
                recordsFiltered: response.data.recordsFiltered || response.data.count || 0,
                recordsTotal: response.data.recordsTotal || response.data.count || 0,
                draw: response.data.draw
            };
        } catch (error) {
            console.error('Erreur dans DetailInventaireService.getDetailInventaireEmplacement:', error);
            if (axios.isAxiosError(error)) {
                return {
                    status: error.response?.status ?? 500,
                    message: error.response?.data?.message ?? 'An error occurred while fetching inventaire details.'
                };
            }
            return {
                status: 500,
                message: 'An unexpected error occurred.'
            };
        }
    },


    async launchInventaire(ids: number[]) {
        try {
            const response = await axiosInstance.post(`${API_URL}/update-start-at/`,ids);
            return response.data;
        } catch (error) {
            console.error('Erreur dans DetailInventaireService.launchInventaire:', error);
        }
    }
};

export default DetailInventaireService;
